import {
  FEEDBACK_CONSENT_LABEL_DOWNVOTE,
  FEEDBACK_CONSENT_LABEL_UPVOTE,
} from '@littlebird/ui-utils';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/dialog';
import { XStack } from '@/components/ui/layout/layout';
import { VoteDataDirection, type VoteDataDirectionType } from '@/query/vote';

import TextArea from '../ui/TextArea';

import { type BaseModalFCProps, type ModalFC } from './modalsManager';

export interface VoteMessageModalProps extends BaseModalFCProps {
  onVote: (reason: string, includeChatHistory: boolean) => void;
  textInputHint: string;
  direction: VoteDataDirectionType;
}

export const VoteMessageModal: ModalFC<VoteMessageModalProps> = ({
  onVote,
  onOpenChange,
  open,
  textInputHint,
  direction,
}) => {
  const [feedback, setFeedback] = useState('');
  const [includeChatHistory, setIncludeChatHistory] = useState(
    direction === VoteDataDirection.DOWN
  );

  const handleSubmit = () => {
    onVote(feedback.trim(), includeChatHistory);
    onOpenChange(false);
    toast.info('Thank you for your feedback!');
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Send feedback</Dialog.Title>
          <Dialog.Description>
            We review all feedback to improve your experience with Littlebird
          </Dialog.Description>
        </Dialog.Header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <TextArea
            value={feedback}
            className="min-h-32"
            onChange={(event) => setFeedback(event.target.value)}
            placeholder={textInputHint}
            autoFocus
          />
        </form>

        <XStack className="items-center gap-2 mt-3">
          <Checkbox
            id="include-chat-history"
            checked={includeChatHistory}
            onCheckedChange={(checked) =>
              setIncludeChatHistory(checked === true)
            }
          />
          <label
            htmlFor="include-chat-history"
            className="text-sm text-foreground-muted cursor-pointer select-none"
          >
            {direction === VoteDataDirection.UP
              ? FEEDBACK_CONSENT_LABEL_UPVOTE
              : FEEDBACK_CONSENT_LABEL_DOWNVOTE}
          </label>
        </XStack>

        <Dialog.Footer>
          <Button
            variant="secondary"
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            Send feedback
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
